import { Box, FlexBox, Typography } from '@vp/swan';
import { b as buildExports } from './_virtual-index.js';
import React from 'react';
import './ep-pricing-esm-components-price-price.component.js';
import { AlignVAT } from './ep-pricing-esm-types-vat.types.js';
import { DifferentialPrice } from './ep-pricing-esm-components-differential-price-differential-price.component.js';
import './ep-pricing-esm-components-unit-price-unit-price.component.js';
import './ep-pricing-esm-components-units-count-units-count.component.js';
import './ep-pricing-esm-components-unit-price-with-quantity-unit-price-with-quantity.component.js';
import './ep-pricing-esm-components-vat-message-vat-message.component.js';
import { DifferentialPriceForQuantity } from './ep-pricing-esm-components-differential-price-for-quantity-differential-price-for-quantity.component.js';
import { TotalPrice } from './ep-pricing-esm-components-total-price-total-price.component.js';
import { FromPrice } from './ep-pricing-esm-components-from-price-from-price.component.js';
import './ep-pricing-esm-components-price-summary-price-summary.component.js';
import './ep-pricing-esm-components-setup-fee-setup-fee.component.js';
import './ep-pricing-esm-components-range-price-range-price.component.js';
import './ep-pricing-esm-components-volume-discount-volume-discount.component.js';
import { SHOW_ELEV8_CORE_PRICES } from './src-constants-featureFlags.js';
import { defaultTenant } from './src-constants-global.js';
import { DisplayStrategyConfig } from './src-constants-priceDisplayStrategy.js';
import { useFeatureToggle } from './src-hooks-useFeatureToggle.js';
import { LocalizationsProvider, useLocalization } from './src-hooks-useLocalizations.js';
import { getLocalizationsFor } from './src-localization-getLocalizations.js';
import { PageId } from './src-types-pageId.js';
import { PricePresentationType } from './src-types-prices.js';
import { getDifferentialPriceForQuantityConfig, getDifferentialPriceConfig, getCombinedPriceConfig, getQuantityPrice, getPlusMinusPrice, getAsLowAsPerPiecePrice, getRawAndUnitPrice, getPrice, getPriceWithQtyAndUnit, getPriceForQuantityUnits, getUnitRangedPrice, getRangedPrice, getQuantityFromPrice, getPricePerUnit } from './src-util-price-transform.js';
import { SetVersionOnWindow } from './src-util-version-logger.js';
import CSRWrapper from './src-components-CSRWrapper-index.js';
import { MinimumOrderableQuantity } from './src-components-MinimumOrderableQuantity.js';
import PriceErrorBoundary from './src-components-PriceErrorBoundary.js';
import PriceLoading from './src-components-PriceLoading.js';
import Savings from './src-components-Savings.js';
import { SetupFee } from './src-components-SetupFee.js';

const PricePresentationCore = ({
  presentationType,
  priceProperties,
  placeHolderText = "",
  prices,
  pageId,
  className
}) => {
  const { t } = useLocalization();
  const { locale, vatInc } = priceProperties;
  const tenant = priceProperties.tenant || defaultTenant;
  SetVersionOnWindow();
  const { isFeatureFlagEnabled } = useFeatureToggle();
  const showElev8PricePresentation = isFeatureFlagEnabled(SHOW_ELEV8_CORE_PRICES);
  const placeholderSkeletonText = /* @__PURE__ */ React.createElement(PriceLoading, null);
  const renderPriceComponent = () => {
    switch (presentationType) {
      case PricePresentationType.PricePerUnit: {
        const priceInfo = getPricePerUnit(prices, priceProperties);
        return /* @__PURE__ */ React.createElement(buildExports.PricePerUnit, { placeHolderText: placeholderSkeletonText, pricingInfo: priceInfo });
      }
      // V2 UPDATES: Add quantity from wrapper here - or should we migrate both to quantity starting at price?
      case PricePresentationType.QtyStartingAtPrice: {
        const priceInfo = getQuantityPrice(prices, priceProperties);
        return /* @__PURE__ */ React.createElement(
          buildExports.QtyStartingAtPrice,
          {
            placeHolderText: placeholderSkeletonText,
            pricingInfo: priceInfo,
            classNameWrapper: className
          }
        );
      }
      case PricePresentationType.QtyFromPrice: {
        const priceInfo = getQuantityFromPrice(prices, priceProperties);
        return /* @__PURE__ */ React.createElement(
          buildExports.QtyFromPrice,
          {
            placeHolderText: placeholderSkeletonText,
            pricingInfo: priceInfo,
            classNameWrapper: className
          }
        );
      }
      case PricePresentationType.RangePrice: {
        const priceInfo = getRangedPrice(prices, priceProperties);
        return /* @__PURE__ */ React.createElement(
          buildExports.RangePrice,
          {
            placeHolderText: placeholderSkeletonText,
            pricingInfo: priceInfo,
            splitInMultipleLines: true
          }
        );
      }
      case PricePresentationType.UnitRangePrice: {
        const priceInfo = getUnitRangedPrice(prices, priceProperties);
        const isDiscounted = prices.lowestDiscountPrice.taxed < prices.lowestListPrice.taxed;
        return /* @__PURE__ */ React.createElement(FlexBox, { flexDirection: "column" }, /* @__PURE__ */ React.createElement(FlexBox, { flexDirection: "row", alignItems: isDiscounted ? "flex-end" : "center" }, /* @__PURE__ */ React.createElement(
          buildExports.RangePrice,
          {
            placeHolderText: placeholderSkeletonText,
            pricingInfo: {
              ...priceInfo,
              hideVatMessage: true,
              hideShippingMessageInVatMessage: true
            },
            splitInMultipleLines: true
          }
        ), "\xA0", /* @__PURE__ */ React.createElement(Typography, { fontSize: "small", paddingBottom: isDiscounted ? "1" : "0" }, `/ ${t("price-platform.unit")}`)), /* @__PURE__ */ React.createElement(Typography, { fontSize: "small" }, /* @__PURE__ */ React.createElement(
          buildExports.VatMessage,
          {
            pricingInfo: {
              vatInc,
              culture: locale
            },
            tenant,
            placeHolderText: ""
          }
        ), !priceProperties.hideMinimumOrderableQuantity && priceProperties.quantity && /* @__PURE__ */ React.createElement(MinimumOrderableQuantity, { quantity: priceProperties.quantity }), !priceProperties.hideSetupFee && prices.setupFee && /* @__PURE__ */ React.createElement(SetupFee, { priceProperties, setupFee: prices.setupFee })));
      }
      case PricePresentationType.PriceForQtyUnits: {
        const priceInfo = getPriceForQuantityUnits(prices, priceProperties);
        return /* @__PURE__ */ React.createElement(
          buildExports.PriceForQtyUnits,
          {
            placeHolderText: placeholderSkeletonText,
            pricingInfo: priceInfo
          }
        );
      }
      case PricePresentationType.PriceWithQtyAndUnit: {
        const priceInfo = getPriceWithQtyAndUnit(prices, priceProperties);
        return /* @__PURE__ */ React.createElement(FlexBox, { flexDirection: "column" }, /* @__PURE__ */ React.createElement(
          buildExports.PriceForQtyUnits,
          {
            placeHolderText: placeholderSkeletonText,
            pricingInfo: {
              ...priceInfo,
              hideVatMessage: true,
              hideShippingMessageInVatMessage: true
            }
          }
        ), priceProperties.quantity > 1 && !priceProperties.hidePerUnitPrice && prices.unitDiscountedPrice && prices.unitListPrice ? /* @__PURE__ */ React.createElement(Typography, { fontSize: "small" }, /* @__PURE__ */ React.createElement(buildExports.PricePerUnit, { placeHolderText, pricingInfo: priceInfo })) : /* @__PURE__ */ React.createElement(Typography, { fontSize: "small" }, /* @__PURE__ */ React.createElement(
          buildExports.VatMessage,
          {
            pricingInfo: {
              vatInc,
              culture: locale
            },
            tenant,
            placeHolderText: ""
          }
        )), priceProperties.quantity > 1 && /* @__PURE__ */ React.createElement(Typography, { fontSize: "small" }, !priceProperties.hideVolumeDiscount && /* @__PURE__ */ React.createElement(Savings, { prices, priceProperties }), !priceProperties.hideSetupFee && /* @__PURE__ */ React.createElement(SetupFee, { priceProperties })));
      }
      // V2 UPDATES: Use this for Raw Price
      // V2 UPDATES: Update or copy this for RawAndPricePerUnit Wrapper (same thing with unit price on next line)
      case PricePresentationType.RawPrice: {
        const priceInfo = getPrice(prices, priceProperties);
        return /* @__PURE__ */ React.createElement(
          buildExports.RawPrice,
          {
            placeHolderText: placeholderSkeletonText,
            pricingInfo: priceInfo,
            classNameWrapper: className
          }
        );
      }
      case PricePresentationType.RawAndPricePerUnit: {
        const priceInfo = getRawAndUnitPrice(prices, priceProperties);
        return /* @__PURE__ */ React.createElement(FlexBox, { flexDirection: "column" }, /* @__PURE__ */ React.createElement(
          buildExports.RawPrice,
          {
            placeHolderText: placeholderSkeletonText,
            pricingInfo: priceInfo,
            classNameWrapper: className
          }
        ), /* @__PURE__ */ React.createElement(buildExports.PricePerUnit, { placeHolderText, pricingInfo: priceInfo }));
      }
      case PricePresentationType.AsLowAsPricePerPiece: {
        const priceInfo = getAsLowAsPerPiecePrice(prices, priceProperties);
        return /* @__PURE__ */ React.createElement(
          buildExports.AsLowAsPricePerPiece,
          {
            placeHolderText: placeholderSkeletonText,
            pricingInfo: priceInfo,
            classNameWrapper: className
          }
        );
      }
      case PricePresentationType.PlusOrMinus: {
        const priceInfo = getPlusMinusPrice(prices, priceProperties);
        return /* @__PURE__ */ React.createElement(
          buildExports.PlusOrMinus,
          {
            placeHolderText: placeholderSkeletonText,
            pricingInfo: priceInfo,
            classNameWrapper: className
          }
        );
      }
      case PricePresentationType.SetupFee: {
        return /* @__PURE__ */ React.createElement(
          SetupFee,
          {
            priceProperties,
            setupFee: prices?.setupFee ? prices.setupFee : void 0
          }
        );
      }
      // Expansion Pack price presentation types
      case PricePresentationType.TotalPrice: {
        if (showElev8PricePresentation || !pageId) {
          const totalPriceConfig = getCombinedPriceConfig(prices, priceProperties);
          return /* @__PURE__ */ React.createElement(
            TotalPrice,
            {
              placeholdertext: placeHolderText,
              totalPriceConfig,
              alignVAT: pageId === PageId.PDC ? AlignVAT.RIGHT : AlignVAT.BOTTOM,
              isTile: pageId === PageId.Gallery || pageId === PageId.SearchDesignTiles
            }
          );
        } else {
          const finalDisplayStrategy = (pageId && DisplayStrategyConfig[pageId]?.legacy) ?? presentationType;
          return /* @__PURE__ */ React.createElement(
            PricePresentationCore,
            {
              presentationType: finalDisplayStrategy,
              priceProperties,
              placeHolderText,
              prices,
              pageId,
              className
            }
          );
        }
      }
      case PricePresentationType.FromPrice: {
        if (showElev8PricePresentation || !pageId) {
          const fromPriceConfig = getCombinedPriceConfig(prices, priceProperties);
          return /* @__PURE__ */ React.createElement(FromPrice, { placeholdertext: placeHolderText, fromPriceConfig });
        } else {
          const priceInfo = getQuantityPrice(prices, priceProperties);
          return /* @__PURE__ */ React.createElement(
            buildExports.QtyStartingAtPrice,
            {
              placeHolderText: placeholderSkeletonText,
              pricingInfo: priceInfo,
              classNameWrapper: className
            }
          );
        }
      }
      case PricePresentationType.DifferentialPrice: {
        const differentialPriceConfig = getDifferentialPriceConfig(prices, priceProperties);
        return /* @__PURE__ */ React.createElement(DifferentialPrice, { placeholdertext: placeHolderText, differentialPriceConfig });
      }
      case PricePresentationType.DifferentialPriceForQuantity: {
        const differentialPriceForQuantityConfig = getDifferentialPriceForQuantityConfig(prices, priceProperties);
        return /* @__PURE__ */ React.createElement(DifferentialPriceForQuantity, { placeholdertext: placeHolderText, differentialPriceForQuantityConfig });
      }
    }
  };
  const canRenderWithoutPrices = presentationType === PricePresentationType.SetupFee;
  if (!prices && !canRenderWithoutPrices) {
    return /* @__PURE__ */ React.createElement(PriceLoading, null);
  }
  return renderPriceComponent();
};
const PricePresentation = (pricePresentationProps) => {
  return /* @__PURE__ */ React.createElement(CSRWrapper, null, /* @__PURE__ */ React.createElement(LocalizationsProvider, { localizations: getLocalizationsFor(pricePresentationProps.priceProperties.locale) }, /* @__PURE__ */ React.createElement(PriceErrorBoundary, { fallbackUi: /* @__PURE__ */ React.createElement(React.Fragment, null) }, /* @__PURE__ */ React.createElement(Box, { "aria-live": "polite", "aria-atomic": "true", "data-testid": "price-presentation-test-id" }, /* @__PURE__ */ React.createElement(PricePresentationCore, { ...pricePresentationProps })))));
};

export { PricePresentation, PricePresentation as default };
//# sourceMappingURL=src-components-PricePresentation-PricePresentation.js.map
